import classNames from 'classnames';
import React, { useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import ExpandedFundList from 'src/components/Buy/BuyFlow/ExpandedFundList/ExpandedFundList';
import FundListActionBarLink from 'src/components/Buy/BuyLanding/FundListActionBarLink';
import If from 'src/components/Shared/If/If';
import Loader from 'src/components/Shared/Loader/Loader';
import StepExpanded from 'src/components/Shared/StepExpanded/StepExpanded';
import TRP from 'src/components/Shared/TRP/TRP';
import config from 'src/config/config';
import { moduleActions } from 'src/redux/modules';
import {
  getExchangeToExistingLoading,
  getExchangeType,
  getExchangeToNewAllowedTickerLookup,
} from 'src/redux/modules/exchangeTransaction';
import { getAutoExchangeToFundList } from 'src/redux/modules/exchangeTransaction/autoExchangeTransaction.selectors';
import { getSystematicActivityRMDs } from 'src/redux/modules/RMD/RMDSetting.selectors.js';
import * as exchangeActions from 'src/redux/modules/exchangeTransaction/exchangeTransaction.module';
import {
  getCollegeExchangeRegistrationTypeInfo,
  getCollegeExchangeToFundList,
  getExchangeFromUnit,
  getExchangeRegistrationTypeInfo,
  getExchangeToFundList,
  isExchangeToNewEligible,
} from 'src/redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import { getHouseholdBalanceTierStatus } from '../../../redux/modules/user/PersonalInformation/personalInformation.selectors';
import { setShowAllFundsStep } from 'src/redux/modules/quickBuy/quickBuy.module';
import { getHiddenAccountNumber } from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import { getAllFundsList } from 'src/redux/modules/funds/AllFunds/allFunds.selectors';
import { ApplicationState } from 'src/redux/modules/types';
import { getUserFunds } from 'src/redux/modules/funds/UserFunds/userFunds.selectors';
import { getRegistrationTypeInfoWithProductTypes } from 'src/redux/modules/user/UserRegistrationTypes/UserRegistrationTypes.selectors';
import { ADD_FUND_MODAL_PROPS, ExchangeType } from 'src/settings/settings';
import { appendBasePath } from 'src/utils/nav.utils';
import {
  getExchangeFromTotalDollars,
  getHasExchangeFromSweep,
} from 'src/redux/modules/exchangeTransaction/exchangeTransaction.selectors.js';
import {
  isAutoExchange,
  isCollegeExchange,
  isExchange,
} from 'src/utils/transactions-util';
import ExchangeToFundEditor from '../ExchangeToFundEditor/ExchangeToFundEditor';
import ExchangeToFundGroupHeader from '../ExchangeToFundGroupHeader/ExchangeToFundGroupHeader';
import { Fund } from '../types';
import './ExchangeToForm.scss';

export interface ExchangeToFormProps {
  actions?;
  isSalesforceRep: boolean;
  customerWebViewEnabled: boolean;
  canAddNewFund?: boolean;
  currentStep?: number;
  eligibleForRMD?: boolean;
  fromFunds;
  fundListActionBarLink?;
  fundListActionBarModalProps?: typeof ADD_FUND_MODAL_PROPS;
  selectedFund: Fund;
  showAddNewFundOrAccount?: boolean;
}

const ExchangeToForm: React.FC<ExchangeToFormProps> = ({
  isSalesforceRep,
  customerWebViewEnabled,
  actions,
  canAddNewFund = true,
  fromFunds,
  fundListActionBarLink = FundListActionBarLink,
  fundListActionBarModalProps = ADD_FUND_MODAL_PROPS.DEFAULT,
  selectedFund,
  eligibleForRMD,
  currentStep,
  showAddNewFundOrAccount = true,
}) => {
  const allFunds = useSelector((state: ApplicationState) =>
    getAllFundsList(state)
  );
  const userFunds = useSelector(getUserFunds);
  const exchangeFromUnit = useSelector(getExchangeFromUnit);
  const exchangeToExistingLoading = useSelector(getExchangeToExistingLoading);
  const hiddenAccountsNumber = useSelector(getHiddenAccountNumber);
  const exchangeType = useSelector(getExchangeType);
  const fromFundTotalAmount = useSelector(getExchangeFromTotalDollars);
  const hasExchangeFromSweep = useSelector(getHasExchangeFromSweep);
  const systematicActivityRMDs = useSelector(getSystematicActivityRMDs);
  const exchangeToNewAllowedTickerLookup = useSelector(
    getExchangeToNewAllowedTickerLookup
  );
  const eligibleFromFundForExchangeToNew = useSelector(isExchangeToNewEligible);

  const exchangeToFundList = useSelector((state) => {
    return isAutoExchange()
      ? getAutoExchangeToFundList(state)
      : isCollegeExchange()
      ? getCollegeExchangeToFundList(state)
      : getExchangeToFundList(state);
  });
  const userRegistrationTypeInfo = useSelector((state) =>
    isExchange()
      ? getExchangeRegistrationTypeInfo(state)
      : isCollegeExchange()
      ? getCollegeExchangeRegistrationTypeInfo(state)
      : getRegistrationTypeInfoWithProductTypes(state)
  );

  const householdBalanceTierStatus = useSelector(getHouseholdBalanceTierStatus);

  // create iClassFundLookup object for use in fund list
  /**
   * @return { [agreementID]: Fund }
   */
  const iClassFundLookUp = useMemo(
    () =>
      exchangeToFundList.reduce((acc, fund) => {
        const currentFundTicker = fund.ticker;
        const iClassVer = allFunds.find(
          (fund) => fund.ticker === currentFundTicker
        );
        const iClassInfo = allFunds.find(
          (fund) => fund.ticker === iClassVer?.correspondingFund?.ticker
        );
        return iClassVer
          ? {
              ...acc,
              [fund.agreementID]: {
                ...iClassInfo,
                exchangeAllowed:
                  exchangeToNewAllowedTickerLookup[
                    iClassVer.correspondingFund?.ticker
                  ],
              },
            }
          : acc;
      }, {}),
    [allFunds, exchangeToFundList, exchangeToNewAllowedTickerLookup]
  );

  return exchangeToExistingLoading ? (
    <div className="loader">
      <Loader isVisible />
    </div>
  ) : (
    <StepExpanded className="exchange-to-form-container">
      <If test={exchangeToFundList?.length === 0}>
        <div className="exchange-to-form-intro">
          <div className="exchange-to-form-zero-banks-msg">
            Based on selections made in Step 1, there are no other eligible{' '}
            {isCollegeExchange() ? '529' : <TRP />} accounts to exchange into.
            To continue with this exchange,{' '}
            <div
              className="exchange-to-form-link"
              onClick={() => actions.setProgress(1)}
            >
              <a className="text-nowrap college-branded-link">go back</a>
            </div>{' '}
            and edit your selections, or you can add a new{' '}
            {isCollegeExchange() ? 'portfolio' : 'fund'} below. If you would
            like to send the proceeds of a{' '}
            {isCollegeExchange() ? 'distribution' : 'sale'} to a verified bank
            account instead of exchanging into another
            {isCollegeExchange() ? ' portfolio' : ' fund'}, visit the{' '}
            {isCollegeExchange() ? (
              <a
                className="text-nowrap college-branded-link"
                href={`${config.url}/usis/transactions/college/distribute`}
              >
                529 distribute
              </a>
            ) : (
              <a
                className="text-nowrap"
                href={`${config.url}${appendBasePath('/sell')}`}
              >
                Mutual Funds Sell
              </a>
            )}{' '}
            page.
          </div>
        </div>
      </If>
      <div
        className={classNames('exchange-to-form-fund-list', {
          'exchange-to-form-single-select':
            exchangeType !== ExchangeType.ONE_TO_MANY,
        })}
      >
        <ExpandedFundList
          showAddNewFundOrAccountEvenIfEmpty
          IRALimit={{ data: { currentYearRemainingContributions: null } }}
          availableFunds={exchangeToFundList}
          currentYearInclusiveRemainingContributions={null}
          customerWebViewEnabled={customerWebViewEnabled}
          exchangeFromFunds={fromFunds}
          fromFundTotalAmount={fromFundTotalAmount}
          fundEditor={() => ExchangeToFundEditor}
          fundGroupHeader={ExchangeToFundGroupHeader}
          hasExchangeFromSweep={hasExchangeFromSweep}
          householdBalanceTierStatus={householdBalanceTierStatus}
          iClassFundLookUp={iClassFundLookUp}
          isSalesforceRep={isSalesforceRep}
          numberOfFunds={exchangeToFundList?.length}
          personalData={{}}
          priorYearInclusiveRemainingContributions={null}
          quickBuy={{}}
          systematicActivityRMDs={systematicActivityRMDs}
          {...{
            actions,
            canAddNewFund,
            currentStep,
            eligibleForRMD,
            exchangeFromUnit,
            exchangeType,
            fromFunds,
            fundListActionBarLink,
            fundListActionBarModalProps,
            hiddenAccountsNumber,
            selectedFund,
            showAddNewFundOrAccount,
            userFunds,
            userRegistrationTypeInfo,
            eligibleFromFundForExchangeToNew,
          }}
        />
      </div>
    </StepExpanded>
  );
};

const mapDispatchToProps = (dispatch) => {
  const {
    deselectExchangeToFund,
    selectExchangeToFund,
    selectExchangeType,
    updateExchangeToFundAmount,
    updateExchangeToFundUnit,
  } = exchangeActions;
  return {
    actions: bindActionCreators(
      {
        ...moduleActions,
        deselectFund: deselectExchangeToFund,
        selectExchangeType,
        selectFund: selectExchangeToFund,
        setShowAllFundsStep,
        updateExchangeFundAmount: updateExchangeToFundAmount,
        updateExchangeFundUnit: updateExchangeToFundUnit,
      },
      dispatch
    ),
  };
};

export default connect(null, mapDispatchToProps)(ExchangeToForm);
